'use client';

import { Canvas } from '@react-three/fiber';
import { ChevronLeft } from 'lucide-react';
import { useRouter } from 'next/navigation';
import React, { Suspense, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PropertyLegendMobile } from '@/components/property-selector/draw-components/property-legend-mobile';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { usePropertySelectorMachine } from '@/state-machine/property-selector/use-property-selector-machine';
import { type PatchedProject, type PatchedPropertyUnit, type Shape } from '@/types';
import { DrawMapObjects } from './draw-components/draw-map-objects';
import { FitBounds } from './draw-components/fit-bounds';
import { useSelectedProperty } from './hooks/use-selected-property';

const DrawReadonly: React.FC<{
  project: PatchedProject;
  properties: PatchedPropertyUnit[];
  openInNewTab?: boolean;
}> = ({ project, properties, openInNewTab }) => {
  const { t, i18n } = useTranslation();
  const { push } = useRouter();
  const { selectedPropertyId, setSelectedPropertyId } = useSelectedProperty();
  const isMobile = useIsMobile();

  const {
    mapConfig: {
      mapImage,
      zoomIntoProperty,
      hoverColor,
      availableColor,
      soldColor,
      colorsSettings,
    },
  } = project ?? ({} as PatchedProject);
  const {
    handlers: { onPointerOver, onPointerOut },
  } = usePropertySelectorMachine({
    shapes: properties.map((p) => (p.demarcationShape as Shape) || []),
    saveShape: async () => {},
  });

  const shouldZoomIn = zoomIntoProperty || isMobile;

  const selectedProperty = properties.find((p) => p.id === selectedPropertyId);

  const onShapeClick = useCallback(
    (property: PatchedPropertyUnit) => {
      if (!project || property.state === 'sold') return;

      if (!shouldZoomIn) {
        const url = `/${i18n.language}/project/${project.slug}/property/${property.slug}`;
        if (openInNewTab) {
          window.open(url);
        } else {
          push(url);
        }
        return;
      }

      if (selectedPropertyId !== property.id) {
        setSelectedPropertyId(property.id);
      } else {
        setSelectedPropertyId(null);
      }
    },
    [
      i18n.language,
      openInNewTab,
      project,
      push,
      selectedPropertyId,
      setSelectedPropertyId,
      shouldZoomIn,
    ],
  );

  return (
    <div className="min-w-full">
      {selectedPropertyId && (
        <Button
          variant="secondary"
          className="z-10 absolute top-3 left-3 justify-start px-2"
          onClick={() => setSelectedPropertyId(null)}
        >
          <ChevronLeft size={20} />
          <span className="block">{t('property.selector.map-view')}</span>
        </Button>
      )}
      <div className="aspect-video">
        <Canvas id="draw-canvas" className="w-auto bg-gray-700" orthographic={true}>
          <Suspense fallback={null}>
            <ambientLight />
            <pointLight position={[10, 10, 10]} />
            <DrawMapObjects
              showLegend={!isMobile}
              selectedDemarcationId={!isMobile ? selectedPropertyId : undefined}
              basemap={mapImage}
              demarcations={properties}
              onPointerOver={!isMobile && selectedPropertyId == null ? onPointerOver : undefined}
              onPointerOut={isMobile ? onPointerOut : undefined}
              onShapeClick={onShapeClick}
              hoverColor={hoverColor}
              availableColor={availableColor}
              soldColor={soldColor}
              colorsSettings={colorsSettings as string}
            />
            {shouldZoomIn && <FitBounds points={selectedProperty?.demarcationShape} />}
          </Suspense>
        </Canvas>
      </div>
      {selectedProperty && isMobile && (
        <PropertyLegendMobile
          availableColor={availableColor}
          soldColor={soldColor}
          property={selectedProperty}
          openInNewTab={openInNewTab}
        />
      )}
    </div>
  );
};

export default DrawReadonly;
